import { getExtendedFormatObject } from '../../content/style';
import { TTLineChartTypes } from '../../config/constants';
import { isObject, cloneObject, unique, roundTo } from '../../utilities';
import getLabelsQuill from './quills/labels';
import getLegendQuill from './quills/legend';
import getTooltipQuill from './quills/tooltip';
import getXAxisQuill from './quills/xAxis';
import getYAxisQuill from './quills/yAxis';

const defaultQuillStyle = getExtendedFormatObject();

// METHOD: TTLineChart [Validator]
export const validateTTLineChart = (content) => {
  const { type, typeSpecific } = content;
  let { lineChart } = typeSpecific.charts23;

  // only validate tt lineCharts
  if (!TTLineChartTypes.includes(type)) return;

  if (!lineChart?.version || lineChart.version < 2) {
    lineChart = getGenesisLineChart(content);
    // Empty Tooltip as Default
    typeSpecific.advancedTooltip.content = JSON.stringify({ ops: [] });

    // Labels
    lineChart.labels.enabled = true;
    lineChart.labels.layout = {
      margin: {
        bottom: 0,
        left: 0,
        top: 0,
        right: 0,
      },
      display: 'desktop',
    };
    lineChart.labels.connectingLineMode = 'missing';
    lineChart.labels.labelsQuill.valueStyle.useDataColor = true;
    lineChart.labels.connectingLinesStyle.useDataColor = true;
    lineChart.labels.connectingLinesStyle.strokeDasharray = 'dotted';
    lineChart.labels.connectingLinesStyle.stroke = '#ddd';
    lineChart.labels.distribute = true;
    delete lineChart.labels.margin;

    // Layout
    lineChart.layout.mobile.breakpoint = 500;
    lineChart.layout.margins.outer.left = 10;
    lineChart.layout.margins.outer.right = 10;

    // Legend
    lineChart.legend.enabled = true;
    lineChart.legend.layout = {
      margin: {
        bottom: 0,
        left: 0,
        top: 0,
        right: 0,
      },
      display: 'mobile',
      position: 'top',
    };
    delete lineChart.legend.margin;

    // Animation
    lineChart.animation.duration = 6;
    lineChart.animation.margin.top = 5;
    lineChart.animation.style.color = '#787878';
    lineChart.animation.style.playback.size = 20;

    // ToolTip
    lineChart.tooltip.enabled = true;
    lineChart.tooltip.layout = {
      margin: {
        bottom: 0,
        left: 0,
        top: 0,
        right: 0,
      },
    };
    lineChart.tooltip.trigger = 'mouseover';
    lineChart.tooltip.connectingLine = true;
    lineChart.tooltip.connectingLineStyle = {
      stroke: '#ddd',
      strokeDasharray: 'solid',
      strokeWidth: 1,
    };
    delete lineChart.tooltip.margin;

    // Lines
    // Enable Dots on Custom Lines
    lineChart.lines.values.map((_line: any) => {
      _line.style.dots.enabled = true;
    });
    lineChart.lines.defaults.emptyLineStyle.strokeDasharray = 'dotted';

    lineChart.lines.defaults.styles = {
      strokeDasharray: 'solid',
      dots: {
        enabled: true,
        shape: 'circle',
        size: 3,
        display: 'last',
        useDataColor: true,
        useDataColorStroke: true,
      },
    };

    // Version
    lineChart.version = 2;
  }

  //
  if (lineChart.version < 3) {
    // lineChart.axis[0].labelsQuill.valueStyle.position = 'center';
    lineChart.axis[1].labelsQuill.valueStyle.position = 'right';

    // Animation Min/Max
    lineChart.animation.slider.min = 0;
    lineChart.animation.slider.max = -1;

    // Tooltip Box Styling
    lineChart.tooltip.layout.box = {
      enabled: false,
      style: {
        maxWidthDesktop: 50,
        maxWidthMobile: 50,
        backgroundColor: '#fff',
        borderWidth: 1,
        borderColor: '#ddd',
      },
    };

    lineChart.version = 3;
  }

  if (lineChart.version < 4) {
    // Animation Autoplay
    // lineChart.animation.autoplay = false;
    lineChart.labels.layout.style = {
      maxWidthDesktop: 25,
      maxWidthMobile: 25,
    };
    lineChart.tooltip.layout.box.style = {
      maxWidthDesktop: 50,
      maxWidthMobile: 50,
    };

    lineChart.legend.layout.show = 'same';

    // Tooltip Box Styling
    lineChart.tooltip.layout.box.style.shadowSize = 0;
    lineChart.tooltip.layout.box.style.shadowBlur = 0;
    lineChart.tooltip.layout.box.style.shadowColor = 'rgba(0,0,0,0.3)';
    lineChart.tooltip.layout.box.style.borderWidth = 1;
    lineChart.tooltip.layout.box.style.borderColor = '#ddd';

    lineChart.version = 4;
  }

  if (lineChart.version < 5) {
    // Annotations
    lineChart.annotations = {
      defaults: {
        styles: {},
      },
      values: [],
    };
    lineChart.ranges = {
      defaults: {
        styles: {},
      },
      values: [],
    };

    delete lineChart.axis[0].dataItems;
    delete lineChart.axis[1].dataItems;

    lineChart.version = 5;
  }

  if (lineChart.version < 6) {
    // Slider Overflow FIX
    const animation = lineChart.animation;
    if (animation.type === 'continuos' && animation.slider.max > 100) {
      lineChart.animation.slider.max = -1;
    }
    lineChart.version = 6;
  }

  if (lineChart.version < 7) {
    lineChart.axis[1].hideZero = false;
    lineChart.labels.connectingLineMode = 'always';

    lineChart.version = 7;
  }

  if (lineChart.version < 8) {
    lineChart.annotations.defaults.styles = {
      ...defaultAnnotationProps,
    };
    lineChart.ranges.defaults.styles = {
      ...defaultRangesProps,
    };
    lineChart.version = 8;
  }

  if (lineChart.version < 9) {
    // Reset mobile Legend style
    lineChart.annotations.defaults.styles.mobileLegendStyle.padding = {
      top: 0,
      topUnit: 'px',
      right: 0,
      rightUnit: 'px',
      bottom: 0,
      bottomUnit: 'px',
      left: 0,
      leftUnit: 'px',
    };
    lineChart.annotations.defaults.styles.mobileLegendStyle.rowGap = 0;
    lineChart.annotations.defaults.styles.mobileLegendStyle.columnGap = 0;
    // Focus general settings
    delete lineChart.annotations.defaults.styles.focused;
    delete lineChart.ranges.defaults.styles.focused;
    // Un-focus all the rest
    for (let i = 0; i < lineChart.annotations.values.length; i++) {
      delete lineChart.annotations.values[i].focused;
    }
    for (let i = 0; i < lineChart.ranges.values.length; i++) {
      delete lineChart.ranges.values[i].focused;
    }

    lineChart.version = 9;
  }

  if (lineChart.version < 10) {
    // Map Y Axis 'showOnlyCustomQuills' boolean to the 'labelsShowMode' string
    if (lineChart.axis[1].showOnlyCustomQuills) {
      lineChart.axis[1].labelsShowMode = 'customQuills';
    } else {
      lineChart.axis[1].labelsShowMode = 'visibleData';
    }

    lineChart.version = 10;
  }

  if (lineChart.version < 11) {
    // Set Interpolation on Date Axis to true by default
    lineChart.axis[0].interpolate = true;
    lineChart.version = 11;
  }

  if (lineChart.version < 12) {
    // Unify line color from categorical obj to line obj
    content.typeSpecific.style.colors2.type = 'categorical';

    lineChart.lines.values = lineChart.lines.values.filter((line: any) => !!line);

    const { values, fallback } = content.typeSpecific.style.colors2.categorical;
    for (const color of values) {
      const line = lineChart.lines.values.find((li: any) => li.id === color.value);
      // Ensure styles exists
      if (!line) {
        lineChart.lines.values.push({
          id: color.value,
          label: color.value,
          styles: {},
        });
      } else {
        if (!line.styles) line.styles = {};
      }
    }
    lineChart.version = 12;
  }

  if (lineChart.version < 13) {
    const { values, fieldName } = content.typeSpecific.style.colors2.categorical;
    if (fieldName === 'auto23degreesseries') {
      for (const color of values) {
        color.value = 'Series';
      }
    }

    lineChart.version = 13; // TODO uncomment this when the version is finished
  }

  typeSpecific.charts23.lineChart = lineChart;
};

export const getDimensionFromType = (dimensions: any, dimType: 'label' | 'group' | 'value', contentType: string) => {
  const groupA = ['areachart', 'linechart', 'ttcharts-linechart'];
  const groupB = ['barchart', 'horizontalbarchart', 'donutchart', 'parliamentchart', 'piechart'];
  const groupC = ['groupedbarchart', 'stackedbarchart', 'bar-grouped-horizontal', 'bar-stacked-horizontal'];
  const groupD = ['choropleth', 'bubblemap', 'extrudemap', 'multimap'];

  let dimName;
  if (dimType === 'label') {
    if (groupA.includes(contentType)) dimName = 'xAxis';
    if (groupB.includes(contentType)) dimName = 'xAxis';
    if (groupC.includes(contentType)) dimName = 'groupAxis';
    if (groupD.includes(contentType)) dimName = 'iso';
  } else if (dimType === 'group') {
    if (groupD.includes(contentType)) return;
    if (groupA.includes(contentType)) dimName = 'labelAxis';
    if (groupB.includes(contentType)) return;
    if (groupC.includes(contentType)) dimName = 'xAxis';
  } else if (dimType === 'value') {
    dimName = groupD.includes(contentType) ? 'value' : 'yAxis';
  } else {
    console.log('[getDimensionFromType]', 'bad dimType', dimType, contentType);
  }

  const dimension = dimensions.find((obj) => obj.dimName === dimName);
  if (!dimension) {
    console.log('[getDimensionFromType]', 'could not find dimension', dimName, dimType, contentType, dimensions);
    return;
  }

  return dimension;
};

export const getFields = (_dimensions: any) => {
  const labelDim = getDimensionFromType(_dimensions, 'label', 'ttcharts-linechart');
  const valueDim = getDimensionFromType(_dimensions, 'value', 'ttcharts-linechart');
  const groupDim = getDimensionFromType(_dimensions, 'group', 'ttcharts-linechart');

  let labelField: string = groupDim.fieldName;
  let xAxisField: string = labelDim.fieldName;
  let yAxisField: string = valueDim.fieldName;

  return { labelField, xAxisField, yAxisField };
};

export const getGenesisLineChart = (content: any) => {
  const dimensions = content.typeSpecific.dimensions;
  const fields = content.typeSpecific.fields;
  const { labelField, xAxisField, yAxisField } = getFields(dimensions);

  const labelsQuillStyle = cloneObject(defaultQuillStyle);
  labelsQuillStyle.shadow = {
    x: 0,
    y: 0,
    blur: 0,
    spread: 0,
    color: 'transparent',
  };

  const tooltipQuillStyle = cloneObject(defaultQuillStyle);
  tooltipQuillStyle.shadow = {
    x: 0,
    y: 0,
    blur: 0,
    spread: 4,
    color: 'transparent',
  };

  const genesisLineChart = {
    layout: {
      margins: {
        outer: {
          bottom: 0,
          left: 0,
          top: 0,
          right: 0,
        },
        inner: {
          bottom: 0,
          left: 0,
          top: 0,
          right: 0,
        },
      },
      // TODO Juan maybe breakpoints: { mobile: 540 }
      mobile: {
        breakpoint: 540,
      },
    },
    lines: {
      defaults: {
        styles: {
          dots: {
            enabled: true,
            display: 'last',
            size: 3,
            shape: 'circle',
            strokeWidth: 0,
          },
        },
        emptyLineStyle: {
          strokeWidth: 1,
          strokeDasharray: 'solid',
        },
      },
      values: [],
      emptyLine: 'custom', // 'default' | 'empty' | 'custom'
    },
    areas: {
      defaults: {
        styles: {},
      },
      values: [],
    },
    axis: [
      {
        enabled: true,
        type: fields[xAxisField].dataType,
        position: 'bottom',
        rotation: 0,
        dataItems: [],
        firstPlacement: 'center',
        lastPlacement: 'center',
        inverted: false,
        title: {
          enabled: false,
          quill: '{"ops":[]}',
          valueStyle: cloneObject(defaultQuillStyle),
          quillBackground: {
            backgroundColor: '#ffffff',
          },
        },
        ticks: true,
        ticksStyle: {
          stroke: '#ddd',
          strokeWidth: 1,
          strokeDasharray: 'solid',
        },
        line: true,
        lineStyle: {
          stroke: '#ddd',
          strokeWidth: 1,
          strokeDasharray: 'solid',
        },
        labelsQuill: {
          // quill: '{"ops":[]}',
          quill: JSON.stringify(getXAxisQuill(xAxisField, fields)),
          valueStyle: cloneObject(defaultQuillStyle),
          quillBackground: {
            backgroundColor: '#ffffff',
          },
        },
        gridLines: false,
        gridLinesStyle: {
          stroke: '#ddd',
          strokeWidth: 1,
          strokeDasharray: 'dashed',
        },
        customQuills: [],
        tickToLabel: 0,
      },
      {
        enabled: true,
        type: 'number',
        position: 'left',
        rotation: 0,
        dataItems: [],
        firstPlacement: 'center',
        lastPlacement: 'center',
        inverted: false,
        title: {
          enabled: false,
          quill: '{"ops":[]}',
          valueStyle: cloneObject(defaultQuillStyle),
          quillBackground: {
            backgroundColor: '#ffffff',
          },
        },
        ticks: false,
        ticksStyle: {
          stroke: '#ddd',
          strokeWidth: 1,
          strokeDasharray: 'solid',
        },
        tickPlacement: 'center',
        line: true,
        lineStyle: {
          stroke: '#ddd',
          strokeWidth: 1,
          strokeDasharray: 'solid',
        },
        labelsQuill: {
          // quill: '{"ops":[]}',
          quill: JSON.stringify(getYAxisQuill(yAxisField, fields)),
          valueStyle: cloneObject(defaultQuillStyle),
          quillBackground: {
            backgroundColor: '#ffffff',
          },
        },
        gridLines: true,
        gridLinesStyle: {
          stroke: '#ddd',
          strokeWidth: 1,
          strokeDasharray: 'dashed',
        },
        customQuills: [],
        tickToLabel: 0,
      },
    ],
    animation: {
      enabled: true,
      duration: 4,
      autoplay: false,
      controls: {
        enabled: true,
      },
      slider: {
        enabled: true,
        min: 0,
        max: -1,
      },
      type: 'discrete', // 'continuos' | 'discrete'
      axisBehavior: 'race', // 'race' | 'reveal',
      margin: {
        bottom: 0,
        left: 0,
        top: 0,
        right: 0,
      },
      style: {
        color: '#0073e6',
        playback: {
          size: 24,
        },
        slider: {
          tooltips: {
            backgroundColor: '#444',
            style: { ...cloneObject(defaultQuillStyle), color: '#fff' },
          },
        },
      },
    },
    legend: {
      enabled: true,
      layout: {
        display: 'always', // always | desktop | mobile
        position: 'top', // top | bottom
        margin: {
          bottom: 0,
          left: 0,
          top: 0,
          right: 0,
        },
        show: 'same', // same | line | icon | line_icon
      },
      labelsQuill: {
        // quill: '{"ops":[]}',
        quill: JSON.stringify(getLegendQuill(labelField, xAxisField, yAxisField, fields)),
        valueStyle: cloneObject(defaultQuillStyle),
        quillBackground: {
          backgroundColor: '#ffffff',
        },
      },
      customQuills: [],
    },
    tooltip: {
      enabled: true,
      layout: {
        margin: {
          bottom: 0,
          left: 0,
          top: 0,
          right: 0,
        },
        box: {
          enabled: false,
          style: {
            maxWidth: -1,
            backgroundColor: '#ffffff',
            borderWidth: 1,
            borderColor: '#777',
          },
        },
      },
      labelsQuill: {
        // quill: '{"ops":[]}',
        quill: JSON.stringify(getTooltipQuill(labelField, xAxisField, yAxisField, fields)),
        valueStyle: { ...tooltipQuillStyle, useDataColor: true },
        quillBackground: {
          backgroundColor: '#ffffff',
        },
      },
      customQuills: [],
      trigger: 'mouseover',
      connectingLine: true,
      connectingLineStyle: {
        stroke: '#ddd',
        strokeWidth: 1,
        strokeDasharray: 'solid',
        dots: {
          enabled: true,
          shape: 'circle',
          size: 3,
          strokeWidth: 0,
          display: 'all',
        },
      },
    },
    labels: {
      enabled: true,
      layout: {
        display: 'always', // always | desktop | mobile
        margin: {
          bottom: 0,
          left: 0,
          top: 0,
          right: 0,
        },
        style: {
          maxWidthDesktop: 25,
          maxWidthMobile: 25,
        },
      },
      labelsQuill: {
        // quill: '{"ops":[]}',
        quill: JSON.stringify(getLabelsQuill(labelField, xAxisField, yAxisField, fields)),
        valueStyle: { ...cloneObject(defaultQuillStyle), useDataColor: true },
        quillBackground: {
          backgroundColor: '#ffffff',
        },
      },
      customQuills: [],
      connectingLines: true,
      connectingLinesStyle: {
        stroke: '#ddd',
        strokeWidth: 1,
        strokeDasharray: 'dotted',
      },
      distribute: false,
    },
    palette: {
      // Maybe Temporary
      colors: [],
    },
  };

  return genesisLineChart;
};

export const defaultAnnotationProps = {
  quill: {
    valueStyle: cloneObject(defaultQuillStyle),
    quillBackground: { background: '#ffffff' },
  },
  display: 'always', // always | desktop | mobile
  onAnimation: 'onPoint', // always | onPoint | on
  padding: {
    top: 5,
    topUnit: 'px',
    right: 5,
    rightUnit: 'px',
    bottom: 5,
    bottomUnit: 'px',
    left: 5,
    leftUnit: 'px',
  },
  background: '#fff',
  radius: 0,
  shadow: {
    x: 0,
    y: 0,
    blur: 0,
    spread: 0,
    color: 'transparent',
  },
  anchor: 'center', //top-left|top-center|top-right|center-left|...|bottom-right
  border: {
    size: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
    color: '#bbb',
    type: 'solid',
  },
  mobileLegend: true,
  mobileLegendStyle: {
    padding: {
      top: 0,
      topUnit: 'px',
      right: 0,
      rightUnit: 'px',
      bottom: 0,
      bottomUnit: 'px',
      left: 0,
      leftUnit: 'px',
    },
    color: '#000',
    size: 13,
    weight: '700',
    fontFamily: 'manrope',
    width: 50,
  },
  line: true,
  lineStyle: {},
  arrow: 'simple',
  curve: 'linear',
  pointOffset: 0,
  circle: false,
  circleStyle: {},
  circleSize: 15,
};

export const defaultRangesProps = {
  quill: {
    valueStyle: cloneObject(defaultQuillStyle),
    quillBackground: { background: '#ffffff' },
  },
  direction: 'vertical', // Vertical is default
  fill: 'rgba(204, 204, 204, 0.2)',
  fillOpacity: 0.2,
  type: 'range',
  quillAnchor: 'center',
  quillRotation: 0,
  mobileLegend: true,
  mobileLegendStyle: {
    padding: {
      top: 0,
      topUnit: 'px',
      right: 0,
      rightUnit: 'px',
      bottom: 0,
      bottomUnit: 'px',
      left: 0,
      leftUnit: 'px',
    },
    color: '#000',
    size: 13,
    weight: '700',
    fontFamily: 'manrope',
    width: 50,
  },
  padding: {
    top: 0,
    topUnit: 'px',
    right: 0,
    rightUnit: 'px',
    bottom: 0,
    bottomUnit: 'px',
    left: 0,
    leftUnit: 'px',
  },
};
